import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Title, 
  Card, 
  Table, 
  Badge, 
  Group, 
  Text, 
  Switch, 
  TextInput, 
  Button, 
  Stack, 
  ActionIcon, 
  Tooltip,
  Paper,
  Divider,
  LoadingOverlay,
  TagsInput
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { HiOutlineRefresh, HiOutlineSave } from 'react-icons/hi';
import axios from 'axios';

const CronManagement = () => {
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, logsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/monitor/cron-configs`),
        axios.get(`${process.env.REACT_APP_API_URL}/monitor/cron-logs`)
      ]);
      setConfigs(configRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo cargar la información de las crons',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateConfig = async (taskName, data) => {
    setSaving(true);
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/monitor/cron-configs/${taskName}`, data);
      notifications.show({
        title: 'Éxito',
        message: 'Configuración actualizada correctamente',
        color: 'green'
      });
      fetchData();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo actualizar la configuración',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Gestión de Tareas Programadas</Title>
        <ActionIcon variant="light" color="blue" onClick={fetchData} loading={loading}>
          <HiOutlineRefresh size={20} />
        </ActionIcon>
      </Group>

      <Stack gap="xl">
        {/* Configurations Section */}
        <Card withBorder radius="md" p="xl" style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading || saving} />
          <Title order={4} mb="md">Configuración de Crons</Title>
          <Table verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tarea</Table.Th>
                <Table.Th>Frecuencia (Cron Exp)</Table.Th>
                <Table.Th>Destinatarios</Table.Th>
                <Table.Th>Estado</Table.Th>
                <Table.Th>Acciones</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {configs.map((config) => (
                <CronConfigRow 
                  key={config._id} 
                  config={config} 
                  onSave={(data) => handleUpdateConfig(config.taskName, data)} 
                />
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        {/* Recent Activity Section */}
        <Card withBorder radius="md" p="xl">
          <Title order={4} mb="md">Actividad Reciente</Title>
          <Table verticalSpacing="xs" striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Estado</Table.Th>
                <Table.Th>Tarea</Table.Th>
                <Table.Th>Ejecutado</Table.Th>
                <Table.Th>Resumen</Table.Th>
                <Table.Th>Duración</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {logs.map((log) => (
                <Table.Tr key={log._id}>
                  <Table.Td>
                    <Badge 
                      color={log.status === 'success' ? 'green' : log.status === 'failed' ? 'red' : 'blue'} 
                      size="sm"
                    >
                      {log.status.toUpperCase()}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm" fw={700}>{log.taskName}</Text></Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {new Date(log.startedAt).toLocaleString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={log.error || log.result}>
                      <Text size="sm" truncate="end" style={{ maxWidth: 250 }}>
                        {log.summary || (log.status === 'running' ? 'En progreso...' : 'Sin resumen')}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {log.finishedAt 
                        ? `${((new Date(log.finishedAt) - new Date(log.startedAt)) / 1000).toFixed(2)}s` 
                        : '-'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      </Stack>
    </Container>
  );
};

const CronConfigRow = ({ config, onSave }) => {
  const [schedule, setSchedule] = useState(config.schedule);
  const [recipients, setRecipients] = useState(config.recipients);
  const [isActive, setIsActive] = useState(config.isActive);

  const hasChanges = schedule !== config.schedule || 
                    JSON.stringify(recipients) !== JSON.stringify(config.recipients) || 
                    isActive !== config.isActive;

  return (
    <Table.Tr>
      <Table.Td>
        <Text fw={700} size="sm">{config.taskName}</Text>
      </Table.Td>
      <Table.Td>
        <TextInput 
          size="xs" 
          value={schedule} 
          onChange={(e) => setSchedule(e.currentTarget.value)} 
          placeholder="* * * * *"
        />
      </Table.Td>
      <Table.Td style={{ minWidth: 300 }}>
        <TagsInput 
          size="xs" 
          placeholder="Agregar emails" 
          value={recipients} 
          onChange={setRecipients}
          splitChars={[',', ' ']}
        />
      </Table.Td>
      <Table.Td>
        <Switch 
          checked={isActive} 
          onChange={(e) => setIsActive(e.currentTarget.checked)}
          color="green"
        />
      </Table.Td>
      <Table.Td>
        <Button 
          size="xs" 
          variant="light" 
          leftSection={<HiOutlineSave size={14} />}
          disabled={!hasChanges}
          onClick={() => onSave({ schedule, recipients, isActive })}
        >
          Guardar
        </Button>
      </Table.Td>
    </Table.Tr>
  );
};

export default CronManagement;
